/**
 * Highcharts options helper
 */

import { HighchartsBaseModel } from 'types';
import * as HighchartsJs from 'highcharts';

const SVG_NS = 'http://www.w3.org/2000/svg';

const buildChartDescription = (props: HighchartsBaseModel) => {
	if (props.description) {
		return props.description;
	}

	const parts: string[] = [];

	if (props.title) {
		parts.push(props.title);
	}

	if (props.subtitle) {
		parts.push(props.subtitle);
	}

	const seriesNames =
		props.series
			?.map((series: any) => series?.name)
			.filter(Boolean)
			.join(', ') || '';

	if (seriesNames) {
		parts.push(`Serier: ${seriesNames}.`);
	}

	if (props.xAxisTitle) {
		parts.push(`X-axel: ${props.xAxisTitle}.`);
	}

	if (props.yAxisTitle) {
		parts.push(`Y-axel: ${props.yAxisTitle}.`);
	}

	if (parts.length === 0) {
		return 'Diagram med statistikdata.';
	}

	return parts.join(' ');
};

const setSvgDesc = (chart: any, description: string) => {
	if (typeof document === 'undefined' || !chart?.container) {
		return;
	}

	const svg = chart.container.querySelector('svg');

	if (!svg) {
		return;
	}

	svg.setAttribute('role', 'img');
	svg.setAttribute('aria-label', description);

	let desc = svg.querySelector('desc');

	if (!desc) {
		desc = document.createElementNS(SVG_NS, 'desc');
		svg.insertBefore(desc, svg.firstChild);
	}

	desc.textContent = description;
};

interface GetOptionsFromProps {
	(props: HighchartsBaseModel): HighchartsJs.Options;
}

export const getOptionsFromProps: GetOptionsFromProps = (props) => {
	const {
		chartType,
		title,
		subtitle,
		tooltipValueDecimals,
		tooltipValuePrefix,
		tooltipValueSuffix,
		yAxisTitle,
		yAxisLabelFormat,
		yAxisMin,
		yAxisMax,
		xAxisTitle,
		xAxisLabelFormat,
		xAxisCategories,
		series,
		stacked,
		showLegend,
		chartHeight,
	} = props;
	let stacking: 'normal' | 'percent' | undefined;

	const chartDescription = buildChartDescription(props);

	switch (stacked) {
		case 'percent':
			stacking = 'percent';
			break;
		case true:
		case 'normal':
			stacking = 'normal';
			break;
		default:
			stacking = undefined;
			break;
	}

	const options: Highcharts.Options = {
		title: {
			text: title || undefined,
		},

		colors: [
			'#008A2B',
			'#6B2879',
			'#E05B27',
			'#00636A',
			'#779F32',
			'#231E13',
			'#DD2879',
			'#1F497D',
			'#79766F',
			'#B4135B',
		],
		credits: {
			enabled: false,
		},
		subtitle: {
			text: subtitle || undefined,
		},
		chart: {
			type: chartType,
			style: {
				fontFamily: '"IBM Plex Sans", sans-serif',
			},
			height: chartHeight,
			marginRight: 15,
			events: {
				load: function (this: any) {
					setSvgDesc(this, chartDescription);
				},
				render: function (this: any) {
					setSvgDesc(this, chartDescription);
				},
			},
		},
		yAxis: {
			title: {
				text: yAxisTitle || undefined,
			},
			labels: {
				format: yAxisLabelFormat || undefined,
			},
			min: yAxisMin || undefined,
			max: yAxisMax || undefined,
			minTickInterval: 1,
		},
		xAxis: {
			title: {
				text: xAxisTitle || undefined,
			},
			categories: xAxisCategories || undefined,
			labels: {
				format: xAxisLabelFormat || undefined,
			},
		},
		plotOptions: {
			series: {
				stacking: stacking,
			},
			pie: {
				dataLabels: {},
				tooltip: {
					headerFormat: '<b>{point.key}</b><br/>',
					pointFormat: `${
						tooltipValueSuffix !== '%' ? 'Antal: <b>{point.y}</b><br/>' : ''
					}Andel: <b>{point.percentage:.1f} %</b>`,
				},
			},
		},
		legend: {
			enabled: showLegend !== false,
			maxHeight: 115,
		},
		tooltip: {
			valueDecimals: tooltipValueDecimals,
			valuePrefix: tooltipValuePrefix,
			valueSuffix: tooltipValueSuffix,
		},
		series: series,
		exporting: {
			sourceWidth: 1400,
			sourceHeight: 800,
			buttons: {
				contextButton: {
					enabled: false,
				},
			},
			chartOptions: {
				plotOptions: {
					series: {
						dataLabels: {
							enabled: true,
						},
					},
					pie: {
						size: '50%',
						dataLabels: {
							distance: '120px',
							padding: 0,
							enabled: true,
							format: `{point.name}<br/>${
								tooltipValueSuffix !== '%' ? 'Antal: {point.y}<br/>' : ''
							}Andel: {point.percentage:.1f} %`,
						},
					},
				},
				legend: {
					navigation: {
						enabled: false,
					},
					itemDistance: 100,
				},
			},
		},
	};

	return options;
};
